<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

import type { SaleAssetBusEntity, SaleAssetBusListingItem } from '~/apiClient';

// COMPOSABLES
const { tt } = useTypedI18n();
const { isMobile } = useResponsive();
const { swiperElement, activeIndex } = useSwiperNavigation();
const { getVideoSrc } = useVideoSrc();
const { attachSdpVideoTracker } = useTracking();

// PROPS
interface Props {
    mainAsset: SaleAssetBusEntity | SaleAssetBusListingItem;
    initialSlide?: number;
}

const { mainAsset, initialSlide = 0 } = defineProps<Props>();

// EMITS
const emit = defineEmits<{
    activeIndexChange: [activeIndex: number];
}>();

// DATA
const model = defineModel<boolean>({ default: false });
const videoElementRefs = ref<(HTMLVideoElement | null)[]>([]);
const videoTrackerCleanupFns = ref<VoidFunction[]>([]);
const buttonSize = computed<ButtonProps['size']>(() => (isMobile.value ? 'lg' : 'xl'));
const videos = computed<string[]>(() => {
    const { engine, gearbox, other } = mainAsset.videos;

    return [...engine, ...gearbox, ...other];
});
const title = computed<string | undefined>(() => {
    const labelKey = saleVideoLabelKey(videos.value[activeIndex.value] ?? '');

    return labelKey && tt(labelKey);
});
const { saleVideoAlt } = useA11y();

// METHODS
const setVideoRef = (el: Element | ComponentPublicInstance | null, index: number): void => {
    if (!(el instanceof HTMLVideoElement)) {
        videoTrackerCleanupFns.value?.[index]?.();
        videoElementRefs.value[index] = null;

        return;
    }

    if (videoElementRefs.value[index] === el) return;

    videoElementRefs.value[index] = el;
    videoTrackerCleanupFns.value[index] = attachSdpVideoTracker(el);
};

// WATCHERS
watch(activeIndex, (value, oldValue) => {
    videoElementRefs.value?.[oldValue]?.pause();
    emit('activeIndexChange', value);
});

// LIFECYCLE
onUnmounted(() => {
    videoTrackerCleanupFns.value.forEach(cleanupFn => cleanupFn?.());
});
</script>

<template>
    <SdpLightboxWrapper v-model="model" index-i18n-key="sdp.videoPagination" :index="activeIndex" :total-items="videos.length" :title="title">
        <SdpSwiperNavigation
            class="w-[calc(100%-2.5rem)] max-w-7xl lg:w-[calc(100%-3.5rem)]"
            :swiper-element="swiperElement"
            :active-index="activeIndex"
            :size="buttonSize"
            color="neutral"
            variant="outline"
        />

        <swiper-container
            ref="swiperElement"
            class="aspect-video max-h-full max-w-full flex-1 rounded-xl lg:landscape:max-h-[calc(100%-5rem)]"
            :initial-slide="initialSlide"
            :space-between="24"
            :breakpoints="{
                768: {
                    spaceBetween: 32,
                },
            }"
        >
            <swiper-slide v-for="(video, index) in videos" :key="video">
                <video
                    :ref="(el: Element | ComponentPublicInstance | null) => setVideoRef(el, index)"
                    controls
                    class="aspect-video h-full rounded-xl border object-cover"
                    :aria-label="saleVideoAlt(mainAsset, video)"
                    :src="getVideoSrc(video)"
                />
            </swiper-slide>
        </swiper-container>
    </SdpLightboxWrapper>
</template>
